// src/context/AppContext.js — All shared state, data hooks, theme & actions v13
import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { auth, trading, wallet as walletAPI } from '../services/api';
import { getSocket, authenticateSocket, updateSocketToken, disconnectSocket, onSocketReplaced } from '../hooks/useSocket';

// S-191: how long without a tick before the WS feed is considered stalled and the REST
// fallback takes over. Backend pushes ~12 ticks/sec, so 45s is far beyond any normal gap.
const WS_STALE_MS = 45000;
import {
  useMarketPrices, usePortfolio, useOrders, useWatchlist,
  useAlerts, usePosts, useNotifications, usePnL, useNews,
  useGlobalStats, useToast, useUserStats,
} from '../hooks/useData';
import { G, GD, GL } from '../constants';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  // ── Auth ──────────────────────────────────────────────────
  const [user,     setUser]     = useState(null);
  const [authDone, setAuthDone] = useState(false);

  // ── UI ────────────────────────────────────────────────────
  // IMPROVEMENT 5: Persist dark mode to localStorage — survives page refresh
  const [darkMode, setDarkModeState] = useState(() => {
    try {
      const saved = localStorage.getItem('pc_darkmode');
      return saved !== null ? saved === 'true' : true; // default dark
    } catch { return true; }
  });

  // Wrap setter so every change also writes to localStorage
  // S-045 (2026-05-10) Fix B Part 2: useCallback for stable identity. Previously
  // re-created every render. Uses functional updater form of setDarkModeState
  // to avoid stale closure on `darkMode` (no need for darkMode in deps).
  const setDarkMode = useCallback((val) => {
    setDarkModeState(prev => {
      const next = typeof val === 'function' ? val(prev) : val;
      try { localStorage.setItem('pc_darkmode', String(next)); } catch {}
      return next;
    });
  }, []);
  // ── AI Assistant ─────────────────────────────────────
  const [aiVoice, setAiVoice] = useState(() => {
    try { return localStorage.getItem('pc_ai_voice') || 'punch'; } catch { return 'punch'; }
  });
  // S-045 (2026-05-10) Fix B Part 3: useCallback for stable identity.
  const setAiVoicePersist = useCallback((v) => {
    try { localStorage.setItem('pc_ai_voice', v); } catch {}
    setAiVoice(v);
  }, []);
  const [aiEnabled, setAiEnabled] = useState(() => {
    try { return localStorage.getItem('pc_ai_enabled') !== 'false'; } catch { return true; }
  });
  // S-045 (2026-05-10) Fix B Part 3: useCallback for stable identity.
  const setAiEnabledPersist = useCallback((v) => {
    try { localStorage.setItem('pc_ai_enabled', String(v)); } catch {}
    setAiEnabled(v);
  }, []);

  // ── Page state: persisted to sessionStorage so refresh stays on same page ──
  const [page, setPageState] = useState(() => {
    try { return sessionStorage.getItem('pc_page') || 'home'; } catch { return 'home'; }
  });
  // S-045 (2026-05-10) Fix B: Wrapped setPage in useCallback for stable identity.
  // Previously a regular arrow function recreated every AppContext render, which
  // caused consumer useEffect deps (like ClubDetailPage's `[clubId, user?.id, setPage, toast]`)
  // to invalidate on every price tick → useEffect cleanup+re-run cycle 60+ times in
  // 4-8 seconds → server kicked WS for Message flood (unsubscribe_club 61) →
  // photo render bug (image fetch starved by main-thread churn). Empty deps because
  // setPage doesn't reference any reactive values; sessionStorage and setPageState
  // are stable. Functional reference; behavior unchanged. Critical for Rule H Single
  // Source — there is exactly ONE canonical setPage with one stable identity.
  const setPage = useCallback((p) => {
    try { sessionStorage.setItem('pc_page', p); } catch {}
    setPageState(p);
    setTimeout(() => {
      try { if (typeof fetchWallet === 'function') fetchWallet(); } catch {}
    }, 100);
  }, []);
  const [sidebar,   setSidebar]   = useState(false);
  const [selCoin,   setSelCoin]   = useState(null);

  // ── Modals ────────────────────────────────────────────────
  const [showAd,  setShowAd]  = useState(false);
  const [adTimer, setAdTimer] = useState(5);

  // ── Feature flag: ads disabled by default ───────────────
  const ADS_ENABLED = false; // Set to true when real ad provider integrated
  const [showSub, setShowSub] = useState(false);
  // S-084 (2026-05-28): Context-aware modal — different content per trigger reason.
  //   'daily_limit' (default) → free user hit daily/weekly/monthly trade limit
  //   'leverage'              → user wants higher leverage (paid users routed via TradePage)
  //   'ads_disabled'          → user tried to watch ad but ads off (free tier fallback)
  // null/undefined falls through to 'daily_limit' (safety default).
  const [subModalContext, setSubModalContext] = useState(null);

  // ── Toast ─────────────────────────────────────────────────
  const { toasts, show: toast } = useToast();

  // ── Gamification state ──────────────────────────────────
  const [userLevel,    setUserLevel]    = useState(null);
  const [dailyClaimed, setDailyClaimed] = useState(false);
  const [gCoins,          setGCoins]          = useState(0);

  // ── Economy Wallet v140: 3 currencies ────────────────────
  // ─────────────────────────────────────────────────────────────
  // PERMANENT CURRENCY DEFINITIONS (v141 — never change these names):
  //   currency  ($)  → virtual trading money, buy profile items
  //   diamonds  (💎) → premium currency, real money purchase
  //   coins     (🪙) → achievements/leaderboard/challenges ONLY (never spent)
  // ─────────────────────────────────────────────────────────────
  const [currency, setCurrency] = useState(0);
  const [diamonds, setDiamonds] = useState(0);
  const [coins,    setCoins]    = useState(0);   // 🪙 Coins — leaderboard/challenges/login rewards

  const fetchWallet = useCallback(async () => {
    // v275 (G4.25a — Doubt #103β): Refactored to use walletAPI.get() instead of
    // direct fetch(). Removed the /store/wallet fallback chain entirely — that
    // was Rule H violation: defensive duplicate-endpoint code where /wallet
    // failure triggered /store/wallet (which returns the SAME data via the SAME
    // wallet.getWallet() backend function). Keeping the fallback provided no
    // resilience benefit (both endpoints fail identically if wallet module is
    // down) and violated single-source rule.
    // Backend /api/economy/wallet deletion (this session) and /api/store/wallet
    // deletion (deferred to PENDING — mobile rollout dependency, see P-005).
    try {
      const d = await walletAPI.get();
      if (d?.success) {
        setCurrency(d.wallet?.currency ?? d.wallet?.cash ?? d.wallet?.coins ?? 0);
        setDiamonds(d.wallet?.diamonds ?? d.wallet?.gems ?? 0);
        setCoins(d.wallet?.coins ?? d.wallet?.points ?? 0);
        // v225: Sync portfolio balance from unified wallet response
        // This ensures Store/Profile/Auction all show same balance as Home
        if (d.wallet?.balance_usdt != null) {
          setPortfolioImmediate(prev => ({
            ...(prev || {}),
            balance_usdt: d.wallet.balance_usdt,
            total_pnl: d.wallet.total_pnl ?? prev?.total_pnl ?? 0,
          }));
        }
      }
    } catch {
      // Silent failure — next 2-min poll will retry; wallet UI shows last
      // known values rather than zero (better UX than fallback to junk endpoint)
    }
  }, []);

  // ── Data hooks ────────────────────────────────────────────
  // v13: Live prices from Binance WebSocket via useSocket
  // v196a FROZEN-TICKER FIX: price_update handler was returning prev unchanged
  // when symbol wasn't found in the array, causing all WS price ticks to be silently
  // dropped if prices_snapshot hadn't arrived yet (race condition). Now we APPEND
  // unknown symbols instead of dropping them, AND fall back to REST prices on first mount.
  const [wsprices, setWsPrices] = useState([]);
  // v225: wsReady = true when live WebSocket prices are flowing (shows "Connecting..." otherwise)
  const wsReady = wsprices.length > 0;
  // S-191: timestamp of the most recent live tick. A ref, so recording a tick never
  // triggers a re-render. Used by the `prices` merge below to detect a stalled feed.
  const lastWsTickRef = useRef(0);
  const [wsStale, setWsStale] = useState(false);
  useEffect(() => {
    const onPrice = (tick) => {
      if (!tick?.symbol) return;
      lastWsTickRef.current = Date.now(); // S-191: liveness marker for the REST fallback
      // Normalize: WS sends "BTCUSDT", we store as "BTC"
      const cleanSymbol = String(tick.symbol).replace('USDT','');
      setWsPrices(prev => {
        const idx = prev.findIndex(p => p.symbol === cleanSymbol);
        if (idx >= 0) {
          // Update existing entry
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            price:      tick.price ?? next[idx].price,
            change_24h: tick.change_24h ?? next[idx].change_24h,
            volume_24h: tick.volume_24h ?? next[idx].volume_24h,
            high_24h:   tick.high_24h   ?? next[idx].high_24h,
            low_24h:    tick.low_24h    ?? next[idx].low_24h,
          };
          return next;
        }
        // v196a: APPEND unknown symbols instead of dropping the tick
        return [...prev, {
          id:         cleanSymbol.toLowerCase(),
          symbol:     cleanSymbol,
          name:       tick.name || cleanSymbol,
          price:      tick.price ?? 0,
          change_24h: tick.change_24h ?? 0,
          volume_24h: tick.volume_24h ?? 0,
          high_24h:   tick.high_24h ?? 0,
          low_24h:    tick.low_24h  ?? 0,
        }];
      });
    };
    // Handle bulk prices snapshot from WS (replaces REST polling)
    const onSnapshot = (data) => {
      if (Array.isArray(data) && data.length > 0) {
        lastWsTickRef.current = Date.now(); // S-191: liveness marker for the REST fallback
        setWsPrices(data.map(t => ({
          id:          t.id || (t.symbol||'').toLowerCase(),
          symbol:      String(t.symbol||'').replace('USDT',''),
          name:        t.name || t.symbol,
          price:       (t.price || t.last) ?? 0,
          change_24h:  t.change_24h ?? t.priceChangePercent ?? 0,
          volume_24h:  t.volume_24h ?? t.volume ?? 0,
          high_24h:    t.high_24h ?? t.high ?? 0,
          low_24h:     t.low_24h  ?? t.low  ?? 0,
        })));
      }
    };
    // S-191: bind through a re-attachable helper instead of a one-shot `socket.on`.
    // disconnectSocket() (logout) nulls the singleton, so the next getSocket() returns a
    // NEW Socket object — and this effect's deps are `[]`, meaning it can never run again.
    // The result: after logout → login the server keeps streaming prices and nothing is
    // listening, so the ticker freezes until a full page reload. Re-attaching on
    // replacement is what keeps these two handlers alive for the life of the app.
    let bound = null;
    const attach = () => {
      const s = getSocket();
      if (!s || typeof s.on !== 'function' || bound === s) return;
      if (bound && typeof bound.off === 'function') {
        try { bound.off('price_update', onPrice); bound.off('prices_snapshot', onSnapshot); } catch (_) {}
      }
      s.on('price_update',    onPrice);
      s.on('prices_snapshot', onSnapshot);
      bound = s;
    };
    attach();
    const unsubReplaced = onSocketReplaced(attach);

    // S-191: mark the feed stale if no tick arrives for a while, so the REST fallback in
    // the `prices` merge below can actually engage. Checked on an interval because
    // staleness is the passage of time, not an event.
    const staleTimer = setInterval(() => {
      const last = lastWsTickRef.current;
      setWsStale(last > 0 && (Date.now() - last) > WS_STALE_MS);
    }, 10000);

    return () => {
      unsubReplaced();
      clearInterval(staleTimer);
      if (bound && typeof bound.off === 'function') {
        try { bound.off('price_update', onPrice); bound.off('prices_snapshot', onSnapshot); } catch (_) {}
      }
      bound = null;
    };
  }, []);  // Fallback to REST-fetched prices until WebSocket is ready
  const { prices: restPrices, loading: pricesLoading, refetch: refetchPrices } = useMarketPrices();
  // v196a: Merge so REST polling can still refresh the ticker if WS gets stuck.
  // S-191 FIX: the comment above promised "if WS array is empty/stale, fall back to REST",
  // but the check was `wsprices.length > 0` — LENGTH only, never staleness. Once WS had
  // delivered a single snapshot the array stayed non-empty forever, so frozen WS prices
  // permanently shadowed the live REST ones and the fallback could never engage. That is
  // what hid the orphaned-listener freeze from web users. Now a feed that has gone quiet
  // for WS_STALE_MS yields to REST — but only if REST actually has data, so a slow first
  // REST load can never blank out a ticker that is working.
  const prices = (wsprices && wsprices.length > 0 && !wsStale)
    ? wsprices
    : ((restPrices && restPrices.length > 0) ? restPrices : wsprices);
  const { data: portfolioData, execute: refetchPort }  = usePortfolio();
  const { data: userStatsData, execute: refetchUserStats } = useUserStats();
  // FIX: pass {} instead of nothing — prevents JSON.stringify(undefined)
  // in useOrders which produces an undefined dep array entry
  const { data: ordersData, execute: refetchOrders }   = useOrders({});
  const { data: pnlData, execute: refetchPnl }           = usePnL();
  const { watchlist, add: addWatch, remove: removeWatch } = useWatchlist();
  const { alerts: alertList, create: createAlert, toggle: toggleAlert, remove: removeAlert } = useAlerts();
  const { posts, likePost, savePost, createPost }      = usePosts();
  const { notifs, unreadCount, markAllRead }            = useNotifications();
  const { data: newsData }                             = useNews();
  const { data: globalData }                           = useGlobalStats();

  // portfolioImmediate: for instant balance update after trade (before refetch completes)
  const [portfolioImmediate, setPortfolioImmediate] = useState(null);
  const portfolio = portfolioImmediate
    ? { ...(portfolioData?.portfolio || {}), ...portfolioImmediate }
    : portfolioData?.portfolio;
  // v225: portfolioImmediate is cleared ONLY when portfolioData actually arrives with new value
  // Old bug: 3-second timeout caused balance to revert before refetch completed
  useEffect(() => {
    if (portfolioData?.portfolio && portfolioImmediate) {
      // Only clear when portfolioData has MATCHING or NEWER balance
      const realBal = portfolioData.portfolio.balance_usdt;
      const immBal = portfolioImmediate.balance_usdt;
      if (realBal != null && immBal != null && Math.abs(realBal - immBal) < 0.01) {
        setPortfolioImmediate(null);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioData?.portfolio?.balance_usdt]);

  // v18: WebSocket — auto-reconnect + auth on login
  const [wsConnected, setWsConnected] = useState(false);
  useEffect(() => {
    const socket = getSocket();
    // FIX: use updateSocketToken so reconnects also carry the latest token
    // authenticateSocket only emits once; updateSocketToken patches io.opts.auth
    const token = localStorage.getItem('pc_token');
    if (user && token) {
      updateSocketToken(token);
    }
    const smartSubscribe = (sock) => {
      // Fix 24: Subscribe to watchlist coins first, fall back to all
      try {
        const saved = JSON.parse(localStorage.getItem('pc_watchlist') || '[]');
        if (Array.isArray(saved) && saved.length > 0) {
          sock.emit('subscribe_prices', { symbols: saved.slice(0, 20) });
        } else {
          sock.emit('subscribe_all_prices');
        }
      } catch { sock.emit('subscribe_all_prices'); }
    };

    const onConnect = () => {
      setWsConnected(true);
      // subscribe_all_prices is now emitted inside getSocket's connect handler
      // but emit again here as fallback (idempotent — backend join is safe to call twice)
      smartSubscribe(socket);
    };
    const onDisconnect = () => setWsConnected(false);
    socket.on('connect',    onConnect);
    socket.on('disconnect', onDisconnect);
    // Sync current state
    if (socket.connected) { setWsConnected(true); smartSubscribe(socket); }
    return () => {
      socket.off('connect',    onConnect);
      socket.off('disconnect', onDisconnect);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Fetch wallet on login, refresh every 60s
  useEffect(() => {
    if (!user) return;
    fetchWallet();
    const t = setInterval(fetchWallet, 120000); // 2 min poll
    // Fix 22: Stagger non-critical fetches to avoid startup spike
    setTimeout(() => { try { refetchOrders(); } catch(_){} },  800);
    setTimeout(() => { try { refetchUserStats(); } catch(_){} }, 1500); // Poll every 2min — WS handles real-time updates
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, fetchWallet]);

  // v13: Listen for backend push events → instant refresh without polling
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // getSocket available from import
    const socket = getSocket();

    const onRefreshPositions = () => {
      // Dispatch custom event so PositionsPage re-fetches its local state
      // This keeps positions data fresh without circular dependency on AppContext
      window.dispatchEvent(new CustomEvent('pc_refresh_positions'));
    };
    let ordersRefreshTimer = null;
    const onRefreshOrders = () => {
      // Debounced: multiple events within 1s collapse to one refetch
      // v275 (G4.25b + P-002): This is the CANONICAL `refresh_orders` debouncer.
      // Per Rule H "no double-fetching": single canonical refetchOrders flow.
      // Now ALSO called from onTradeExecuted + onBalanceUpdate (P-002), so:
      //   • refresh_orders WS event → onRefreshOrders directly
      //   • trade_executed WS event → onTradeExecuted → onRefreshOrders
      //   • balance_update WS event → onBalanceUpdate → onRefreshOrders
      //   • position_closed WS event → onBalanceUpdate → onRefreshOrders
      // All 4 routes share the SAME 1s debounce timer — if multiple fire close
      // together, they coalesce to ONE refetchOrders. Race-free per industry
      // consensus (Rule K research: WebSocket.org, Pluralsight, Ably 2026).
      // OrdersPage no longer needs ANY WS subscribers (P-002 cleanup).
      if (ordersRefreshTimer) clearTimeout(ordersRefreshTimer);
      ordersRefreshTimer = setTimeout(() => { ordersRefreshTimer = null; refetchOrders(); }, 1000);
    };
    // Listen for manual refresh events from trade execution
    const onPortfolioUpdate = () => {}; // NO-OP: balance_update WS carries new_balance directly
    window.addEventListener('portfolioUpdate', onPortfolioUpdate);
    const onTradeExecuted    = (data) => {
      if (data?.new_balance != null) {
        setPortfolioImmediate(prev => ({
          ...(prev || portfolioData?.portfolio || {}),
          balance_usdt: data.new_balance,
          total_pnl: ((prev?.total_pnl ?? portfolioData?.portfolio?.total_pnl ?? 0) + (data.pnl ?? 0)),
        }));
      }
      // P26 (2026-05-23): Update user.trades_left from WS payload for cross-platform sync.
      // When ANY device of the same user trades, the server broadcasts fresh trades_left
      // to ALL connected devices via the user:USER_ID WS room. This is the server-
      // authoritative pattern — single source of truth, instant propagation.
      //
      // Example: Mobile takes trade #1 → server emits trade_executed{trades_left:2}
      //   → webapp's counter immediately updates from 3/3 → 2/3 (no manual refresh!)
      if (data?.trades_left != null) {
        setUser(u => u ? { ...u, trades_left: data.trades_left } : u);
      }
      refetchUserStats();
      fetchWallet();
      // v275 (P-002 — Doubt #116γ): Route through canonical debounced
      // refetchOrders. Reuses the same 1s debounce mechanism as onRefreshOrders
      // (line 295) — race-free per industry consensus (WebSocket.org, Pluralsight,
      // Ably 2026 best practices). If trade_executed + refresh_orders + balance_update
      // all fire within 1s, they coalesce to a SINGLE refetchOrders. Eliminates
      // OrdersPage's per-page subscribers (Rule H violation removed cross-platform).
      onRefreshOrders();
    };
    const onLiquidation = (data) => {
      // Balance comes via balance_update WS event — no refetch needed
      toast(`⚡ LIQUIDATED: ${data.side?.toUpperCase()} ${data.symbol} — PnL: $${data.finalPnL?.toFixed(2) || '0'}`, 'error');
    };
    const onMarginCall = (data) => {
      toast(`⚠️ Margin call! Usage: ${data.marginUsagePct?.toFixed(1)}%`, 'warn');
    };
    const onFunding = (data) => {
      const sign = data.payment >= 0 ? '+' : '';
      toast(`💰 Funding: ${sign}$${data.payment?.toFixed(4)} (${data.symbol})`, 'info');
    };
    const onBalanceUpdate = (data) => {
      if (data?.new_balance != null) {
        setPortfolioImmediate(prev => ({
          ...(prev || portfolioData?.portfolio || {}),
          balance_usdt: data.new_balance,
          total_pnl: ((prev?.total_pnl ?? portfolioData?.portfolio?.total_pnl ?? 0) + (data.pnl ?? 0)),
        }));
      }
      // v275 (P-002 — Doubt #116γ): Route orders refresh through canonical
      // debounced refetchOrders mechanism. Coalesces with trade_executed +
      // refresh_orders if they fire close together — race-free.
      onRefreshOrders();
      setTimeout(() => {
        refetchPort();
        refetchUserStats();
        fetchWallet();
      }, 1500);
    };

    // v225 patch: listen for coin/level events so Topbar updates instantly
    // when gamification awards coins from any source (claim, badge, challenge,
    // ad reward, etc.) — not just on the Gamification page.
    const onCoinsGained = () => { try { fetchWallet(); } catch(_){} };
    const onLevelUp     = () => { try { fetchWallet(); } catch(_){} };

    // S-042 (2026-05-10) Fix #5: Price alert sound — fires ONLY when alert
    // actually triggers (price hits target). Backend emits 'alert_triggered'
    // with { symbol, price, target, condition, sound } payload. App-level
    // listener so user hears the sound regardless of which page they're on.
    // Sandeep wanted this changed FROM creation-time TO trigger-time —
    // industry standard (Robinhood, Coinbase, Binance all behave this way).
    const playAlertSoundFn = (name) => {
      try {
        if (!name || name === 'Silent') return;
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        const freqs = { Bell: 880, Chime: 1046, Alarm: 440 };
        if (!freqs[name]) return;
        o.frequency.value = freqs[name];
        o.type = name === 'Alarm' ? 'sawtooth' : 'sine';
        g.gain.setValueAtTime(0.3, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
        o.start(); o.stop(ctx.currentTime + 0.6);
      } catch {}
    };
    const onAlertTriggered = (data) => {
      try {
        const symbol = data?.symbol || '?';
        const price = data?.price != null ? `$${parseFloat(data.price).toFixed(2)}` : '';
        const condition = data?.condition || 'hit';
        const target = data?.target != null ? `$${parseFloat(data.target).toFixed(2)}` : '';
        // Sound preference saved on the alert — fall back to 'Bell' if not provided.
        playAlertSoundFn(data?.sound || 'Bell');
        toast(`🔔 ${symbol} ${condition} ${target} → ${price}`, 'success');
      } catch {}
    };

    socket.on('trade_executed',    onTradeExecuted);
    socket.on('position_closed',   onBalanceUpdate);  // position_closed → same as balance_update (has pnl + new_balance)
    socket.on('refresh_positions', onRefreshPositions);
    socket.on('refresh_orders',    onRefreshOrders);
    socket.on('liquidation_event', onLiquidation);
    socket.on('margin_call',       onMarginCall);
    socket.on('funding_payment',   onFunding);
    socket.on('balance_update',    onBalanceUpdate);
    socket.on('coins_gained',      onCoinsGained);
    socket.on('level_up',          onLevelUp);
    socket.on('alert_triggered',   onAlertTriggered); // S-042 Fix #5

    return () => {
      socket.off('trade_executed',    onTradeExecuted);
      socket.off('position_closed',   onBalanceUpdate);
      socket.off('refresh_positions', onRefreshPositions);
      socket.off('refresh_orders',    onRefreshOrders);
      socket.off('liquidation_event', onLiquidation);
      socket.off('margin_call',       onMarginCall);
      socket.off('funding_payment',   onFunding);
      socket.off('balance_update',    onBalanceUpdate);
      socket.off('coins_gained',      onCoinsGained);
      socket.off('level_up',          onLevelUp);
      socket.off('alert_triggered',   onAlertTriggered); // S-042 Fix #5
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchPort, toast]);
  const orders      = ordersData?.orders    || [];
  const pnl         = pnlData?.data;
  const newsList    = newsData?.data        || [];
  const globalStats = globalData?.data;

  // ── Auth init ─────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      if (auth.isLoggedIn()) {
        try {
          const res = await auth.me();
          if (res.success) setUser(res.user);
          else auth.logout();
        } catch { auth.logout(); }
      }
      setAuthDone(true);
    })();
  }, []);

  // ── Global auth-expired handler (invalid token → auto logout) ─
  useEffect(() => {
    const handleExpiry = () => {
      auth.logout();
      setUser(null);
      setPage('home');
      toast('Session expired — please log in again.', 'error');
    };
    window.addEventListener('pc_auth_expired', handleExpiry);
    // v225: Re-auth WebSocket after token auto-refresh (keeps position_update events flowing)
    const handleTokenRefresh = (e) => {
      const newToken = e?.detail?.token;
      if (newToken) {
        try { updateSocketToken(newToken); authenticateSocket(newToken); } catch(_) {}
      }
    };
    window.addEventListener('pc_token_refreshed', handleTokenRefresh);
    return () => {
      window.removeEventListener('pc_auth_expired', handleExpiry);
      window.removeEventListener('pc_token_refreshed', handleTokenRefresh);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Default selected coin (run only until first coin set) ─
  const coinSet = useRef(false);
  useEffect(() => {
    if (prices.length && !coinSet.current) {
      setSelCoin(prices[0]);
      coinSet.current = true;
    }
  }, [prices]);

  // ── Theme ─────────────────────────────────────────────────
  const bg   = darkMode ? '#0D0D0D' : '#F5F0E8';
  const bg2  = darkMode ? '#141414' : '#FFF8E7';
  const bg3  = darkMode ? '#1C1C1C' : '#FFFDF5';
  const text = darkMode ? '#F0F0F0' : '#1A1200';
  const tx2  = darkMode ? '#888888' : '#7A6000';
  const bord = darkMode ? '#252525' : '#E8D58A';

  const s = {
    card:    { background:bg3, border:`1px solid ${bord}`, borderRadius:12, padding:16, marginBottom:14 },
    btn:     { background:`linear-gradient(135deg,${GD},${G},${GL})`, color:'#0D0A00', fontWeight:700, border:'none', borderRadius:8, padding:'10px 20px', cursor:'pointer', fontFamily:'inherit', fontSize:14 },
    btnO:    { background:'transparent', color:G, fontWeight:600, border:`1px solid ${G}`, borderRadius:8, padding:'8px 16px', cursor:'pointer', fontFamily:'inherit', fontSize:13 },
    inp:     { background:bg, border:`1px solid ${bord}`, color:text, borderRadius:8, padding:'10px 14px', fontFamily:'inherit', fontSize:14, width:'100%', outline:'none', boxSizing:'border-box' },
    tag:     { display:'inline-block', padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, background:darkMode?'#1A1200':'#FFF3B0', color:G, border:`1px solid ${GD}` },
    navItem: { display:'flex', alignItems:'center', gap:14, padding:'13px 20px', cursor:'pointer', borderRadius:8 },
    pos:     { color:'#0ECB81' },
    neg:     { color:'#F6465D' },
  };

  // ── Watch ad (disabled when ADS_ENABLED=false) ───────────
  const adIntervalRef = useRef(null);

  // v275 (G4.15-WEBAPP-REWARDED-AD-AUDIT): User-cancellable ad-modal escape hatch.
  // Required by AdSense Rewarded-Ad policy implementation rule #2:
  //   "Rewarded Ads must not oblige users to interact with it (for example,
  //    it must be possible to skip or dismiss them)."
  // https://support.google.com/adsense/answer/9121589
  // Closes the modal mid-countdown WITHOUT granting the reward — Reward Rule #6
  // ("Publishers must deliver the promised reward(s) UPON COMPLETION of the required
  // action(s)") is satisfied because the user did NOT complete the watch action.
  const cancelAd = useCallback(() => {
    if (adIntervalRef.current) {
      clearInterval(adIntervalRef.current);
      adIntervalRef.current = null;
    }
    setShowAd(false);
    setAdTimer(0);
  }, []);

  const watchAd = useCallback(() => {
    if (!ADS_ENABLED) {
      // Ads disabled — silently skip, show premium instead
      // S-084: set context so modal shows "Ads Unavailable" message (clearer than
      //  the default "Daily Limit Reached" copy when the trigger was actually
      //  "user wanted to watch ad but ads are off").
      setSubModalContext('ads_disabled');
      setShowSub(true);
      return;
    }
    // Clear any lingering interval from a previous call
    if (adIntervalRef.current) clearInterval(adIntervalRef.current);

    setShowAd(true);
    let t = 30;
    setAdTimer(30);

    adIntervalRef.current = setInterval(() => {
      t--;
      setAdTimer(t);
      if (t <= 0) {
        clearInterval(adIntervalRef.current);
        adIntervalRef.current = null;
        trading.awardAdTrade().then(res => {
          if (res.success) {
            setUser(u => ({
              ...u,
              trades_left:    res.trades_left ?? 1,
              week_ads_left:  res.week_ads_left,
              month_ads_left: res.month_ads_left,
            }));
            toast(`🎉 ${res.message || 'Free trade earned!'}`, 'success');
          } else {
            toast(res.message || 'Could not award trade', 'error');
          }
          setShowAd(false);
        }).catch(() => setShowAd(false));
      }
    }, 1000);
  }, [toast, ADS_ENABLED]);

  // S-045 (2026-05-10) Fix B Part 4: Extract logout to useCallback for stable
  // identity. Was inline arrow in value object → recreated every render.
  const logout = useCallback(() => {
    auth.logout();
    disconnectSocket();
    setUser(null);
    try { sessionStorage.removeItem('pc_page'); } catch {}
    setPageState('home');
  }, []);

  // S-045 (2026-05-10) Fix A: Wrap value in useMemo to give it a stable reference.
  // ROOT CAUSE FIX for clubs photo render bug + WS Message flood + 49 consumer perf.
  //
  // Previously: `const value = { ... }` created a NEW object on every AppContext
  // render (including every Binance price tick via setWsPrices). This made every
  // value.X field a "new identity" from React's perspective, even when the
  // underlying data was unchanged. ClubDetailPage's useEffect deps
  // [clubId, user?.id, setPage, toast] would invalidate every render → cleanup
  // (unsubscribe_club emit) + re-run (subscribe_club emit) cycle 60+ times in
  // 4-8 seconds → backend Message flood → server kicks WS → reconnect storm →
  // main thread overwhelmed → photo image fetch starved → image not rendered
  // until user refreshes (clean mount, no churn).
  //
  // useMemo deps include ALL reactive values referenced in the value object.
  // Stable callbacks (useCallback'd: setPage, setDarkMode, toast, fetchWallet,
  // cancelAd, watchAd, logout, setAiVoicePersist, setAiEnabledPersist) and
  // useState setters (setUser, setShowAd, setSidebar, etc. — always stable per
  // React docs) are NOT in deps — they're already stable and including them
  // would defeat the purpose. ADS_ENABLED is a module-level constant.
  // Theme strings (bg, bg2, bg3, text, tx2, bord) and `s` style object depend
  // on darkMode and are recomputed on darkMode change — `darkMode` in deps
  // covers them transitively (they recompute synchronously alongside).
  //
  // Rule H (Single Source) preserved: there is exactly ONE canonical AppContext
  // value object identity per state-snapshot.
  // Rule J (Multi-perspective): race-safe (no async in value computation),
  // no redundancy (single useMemo), timing-safe (deps capture all reactive vals).
  // Rule K (research): React.dev/reference/react/useMemo + community 2026 best
  // practice for context perf — verified before applying.
  const value = useMemo(() => ({
    // Auth
    user, setUser, authDone,
    // UI
    darkMode, setDarkMode, page, setPage, sidebar, setSidebar,
    selCoin, setSelCoin,
    // Modals
    showAd, setShowAd, adTimer, showSub, setShowSub, ADS_ENABLED,
    // S-084: Context-aware modal — readers (App.js modal) check this to render
    //  tier-specific content; writers (TradePage leverage, watchAd fallback)
    //  set it BEFORE calling setShowSub(true) so the modal shows the right message.
    subModalContext, setSubModalContext,
    cancelAd, // v275 (G4.15): user-cancellable ad-modal escape hatch — AdSense compliance
    // Toast
    toasts, toast,
    // Data
    prices, pricesLoading, refetchPrices, wsConnected,
    portfolio, refetchPort,
    // SSOT: trade counts, PnL, daily limits — use this everywhere
    userStats: userStatsData?.data || null,
    refetchUserStats,
    orders, pnl, newsList, globalStats, refetchOrders, refetchPnl,
    watchlist, addWatch, removeWatch,
    alertList, createAlert, toggleAlert, removeAlert,
    posts, likePost, savePost, createPost,
    // P-018: Removed `isPremium` from AppContext value — it duplicated the
    // `usePremium()` hook with a more restrictive definition (extra
    // `user.plan && user.plan !== 'free'` checks). Per Rule H (Single Source),
    // `usePremium()` from `../hooks/usePremium` is now the only canonical
    // way to derive isPremium. All 4 prior consumers migrated in this session:
    // useFeature.js, ProfilePage.jsx, AlertsPage.jsx, PositionsPage.jsx.
    // Backend's /me endpoint normalizes plan + subscription_expires_at together
    // (auth.js:633,640) so the looser usePremium check is equivalent in production.
    wsReady, // v225: true when live prices flowing via WebSocket
    // Gamification
    userLevel, gCoins, dailyClaimed, setUserLevel, setDailyClaimed, setGCoins,
    // v140 currencies
    // v141 PERMANENT currencies
    currency, diamonds, coins,
    setCurrency, setDiamonds, setCoins,
    fetchWallet,
    // backward compat aliases (do not remove)
    cash: currency, points: coins, gems: diamonds,
    notifs, unreadCount, markAllRead,
    // Theme vars
    bg, bg2, bg3, text, tx2, bord, s,
    // AI Assistant
    aiVoice, setAiVoice: setAiVoicePersist,
    aiEnabled, setAiEnabled: setAiEnabledPersist,
    // Actions
    watchAd,
    logout,
  }), [
    // Auth
    user, authDone,
    // UI
    darkMode, page, sidebar, selCoin,
    // Modals
    showAd, adTimer, showSub,
    // Toast
    toasts,
    // Data
    prices, pricesLoading, wsConnected, portfolio,
    userStatsData,
    orders, pnl, newsList, globalStats,
    watchlist, alertList, posts,
    notifs, unreadCount,
    wsReady,
    // Gamification
    userLevel, gCoins, dailyClaimed,
    // Currencies
    currency, diamonds, coins,
    // Theme (darkMode triggers bg/bg2/bg3/text/tx2/bord/s recompute upstream)
    bg, bg2, bg3, text, tx2, bord, s,
    // AI
    aiVoice, aiEnabled,
    // Stable callbacks intentionally NOT in deps:
    // setPage, setDarkMode, toast, fetchWallet, cancelAd, watchAd, logout,
    // setAiVoicePersist, setAiEnabledPersist, refetchPrices, refetchPort,
    // refetchUserStats, refetchOrders, refetchPnl, addWatch, removeWatch,
    // createAlert, toggleAlert, removeAlert, likePost, savePost, createPost,
    // markAllRead, setUser, setSidebar, setSelCoin, setShowAd, setShowSub,
    // setUserLevel, setDailyClaimed, setGCoins, setCurrency, setDiamonds, setCoins
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => useContext(AppContext);
